import { type ReactNode, useRef, useState, useLayoutEffect } from "react";

import { Row } from "@hightouch/htui";

import { Pagination, type PaginationProps } from "src/ui/table/pagination";
import { Table, type TableProps } from "src/ui/table/table";
import { DefaultPageContainerPadding } from "src/components/layout/page-container";
import { PAGE_CARD_STICKY_TOP } from "src/components/layout/page-card-top-bar";

type Props<RowData extends Record<string, unknown>> = TableProps<RowData> & {
  /** Omit when the page shell already carries the title and controls. */
  header?: ReactNode;
  pagination?: Omit<PaginationProps, "rowsPerPage"> &
    Partial<Pick<PaginationProps, "rowsPerPage">>;
};

export function PageTable<RowData extends Record<string, unknown>>({
  header,
  pagination,
  virtualize = true,
  ...props
}: Props<RowData>) {
  const contentHeaderRef = useRef<HTMLDivElement>(null);
  const [contentHeaderHeight, setContentHeaderHeight] = useState(0);

  // The optional header can mount late; re-attach the observer so the
  // table's sticky offset doesn't stay 0.
  const hasHeader = Boolean(header);
  useLayoutEffect(() => {
    if (!hasHeader) {
      setContentHeaderHeight(0);
      return;
    }
    const element = contentHeaderRef.current;
    if (!element) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const borderBoxSize = entry.borderBoxSize?.[0];
        setContentHeaderHeight(
          borderBoxSize?.blockSize ?? entry.contentRect.height,
        );
      }
    });

    observer.observe(element);
    setContentHeaderHeight(element.offsetHeight);

    return () => observer.disconnect();
  }, [hasHeader]);

  return (
    <>
      {header && (
        <Row
          ref={contentHeaderRef}
          align="center"
          justify="space-between"
          py={4}
          gap={4}
          bg="white"
          zIndex={2}
          px={6}
          width="100%"
          pos="sticky"
          // Below the card's sticky crumb bar, when it has one.
          top={PAGE_CARD_STICKY_TOP}
          borderBottomWidth="hairline"
          borderBottomStyle="solid"
          borderColor="base.divider"
          borderTopLeftRadius="2xl"
          borderTopRightRadius="2xl"
        >
          {header}
        </Row>
      )}
      <Table
        top={`calc(${PAGE_CARD_STICKY_TOP} + ${contentHeaderHeight}px)`}
        virtualize={virtualize}
        {...props}
        placeholder={{ ...(props.placeholder || {}), p: 8 }}
      />
      {pagination && (
        <Row px={DefaultPageContainerPadding.X} py={4}>
          <Pagination rowsPerPage={50} {...pagination} />
        </Row>
      )}
    </>
  );
}
